import { useQuery } from '@tanstack/react-query';

import { http } from '@/shared/api/http';

export interface Group {
  ID: number;
  Name: string;
  Description: string;
  DateCreate: string;
  OwnerID?: string;
}

export function useGroups() {
  const query = useQuery({
    queryKey: ['groups-v2'],
    queryFn: async (): Promise<Group[]> => {
      const response = await http.get<{ result: Group[] }>('/groups/list');
      return response.data.result;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    groups: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useGroupsSearch(search?: string) {
  const query = useQuery({
    queryKey: ['groups', 'search', search],
    queryFn: async (): Promise<Group[]> => {
      const params = search ? { search } : {};
      const response = await http.get<{ result: Group[] }>('/groups/list', {
        params,
      });
      return response.data.result;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: search !== undefined,
  });

  return {
    groups: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useGroupById(groupId: number | string | null) {
  const { groups, isLoading, error } = useGroups();
  const group =
    groups.find((g) => g.ID === parseInt(groupId as string)) || null;

  return {
    group,
    isLoading,
    error,
  };
}

export function useGroup(groupId: number | string) {
  const query = useQuery({
    queryKey: ['group', groupId],
    queryFn: async (): Promise<Group> => {
      const response = await http.get<{ result: Group }>(`/groups/${groupId}`);
      return response.data.result;
    },
    enabled: !!groupId,
    staleTime: 10 * 60 * 1000,
  });

  return {
    group: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
